import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that handles RUT collection and validation for Chilean customers.
 */
class CartPrecheckout extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {((event: Event) => void) | null} */
  #cartUpdateHandler = null;

  /** @type {((event: Event) => void) | null} */
  #dialogOpenHandler = null;

  /** @type {((event: Event) => void) | null} */
  #inputHandler = null;

  /** @type {((event: Event) => Promise<void>) | null} */
  #blurHandler = null;

  connectedCallback() {
    super.connectedCallback();
    // Setup RUT validation and formatting
    this.#setupRutValidation();
    // Restore RUT value from cart attributes and localStorage
    this.#restoreRutValue();
    // Listen for drawer open to re-initialize if needed
    this.#setupDrawerOpenListener();
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   * Re-initializes the component to ensure event listeners are connected.
   */
  updatedCallback() {
    super.updatedCallback();
    // Re-initialize when component is updated (e.g., drawer content re-rendered)
    this.#reinitializeComponent();
  }

  /**
   * Sets up listener for drawer open to ensure component is initialized.
   */
  #setupDrawerOpenListener() {
    // Remove existing listener if any
    if (this.#dialogOpenHandler) {
      document.removeEventListener('dialog:open', this.#dialogOpenHandler);
    }

    this.#dialogOpenHandler = (event) => {
      const drawer = event.target;
      if (drawer instanceof HTMLElement && drawer.classList.contains('cart-drawer')) {
        // Re-initialize component when drawer opens
        // Use setTimeout to ensure DOM is fully updated
        setTimeout(() => {
          this.#reinitializeComponent();
        }, 100);
      }
    };

    document.addEventListener('dialog:open', this.#dialogOpenHandler);
  }

  /**
   * Re-initializes the component when drawer opens.
   * Ensures event listeners are connected even if component was already in DOM.
   */
  #reinitializeComponent() {
    // Use requestAnimationFrame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      const rutInput = this.querySelector('#billing_rut');
      if (!(rutInput instanceof HTMLInputElement)) {
        // If input not found, try again after a short delay
        setTimeout(() => {
          const retryInput = this.querySelector('#billing_rut');
          if (retryInput instanceof HTMLInputElement) {
            this.#setupRutValidation();
            this.#restoreRutValue();
          }
        }, 100);
        return;
      }

      // Always re-setup validation to ensure handlers are connected
      // This handles cases where the component was in DOM but not initialized
      this.#setupRutValidation();

      // Always restore value when drawer opens
      this.#restoreRutValue();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cleanup event listeners
    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdateHandler);
      this.#cartUpdateHandler = null;
    }

    if (this.#dialogOpenHandler) {
      document.removeEventListener('dialog:open', this.#dialogOpenHandler);
      this.#dialogOpenHandler = null;
    }

    // Cleanup input handlers
    const rutInput = this.querySelector('#billing_rut');
    if (rutInput instanceof HTMLInputElement) {
      if (this.#inputHandler) {
        rutInput.removeEventListener('input', this.#inputHandler);
        this.#inputHandler = null;
      }
      if (this.#blurHandler) {
        rutInput.removeEventListener('blur', this.#blurHandler);
        this.#blurHandler = null;
      }
    }

    // Cancel pending requests
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /**
   * Handles updates to cart attributes.
   * @param {Event} event - The input event from form fields.
   */
  updateCartAttributes = (event) => {
    if (!event?.target) return;

    // If the event is from RUT input, validate before updating
    if (
      event.target instanceof HTMLInputElement &&
      event.target.id === 'billing_rut'
    ) {
      this.#validateRut(event.target);
      // Update cart even if invalid (to save partial data), but show error
      this.#updateCartAttributesDebounced(event);
      return;
    }

    // For other fields, use debounced update
    this.#updateCartAttributesDebounced(event);
  };

  /**
   * Updates cart attributes with debounce (for input fields).
   * @param {Event} event - The input event.
   */
  #updateCartAttributesDebounced = debounce(async (event) => {
    await this.#saveRutToCart();
    if (event) {
      cartPerformance.measureFromEvent('precheckout-update:user-action', event);
    }
  }, 500);

  /**
   * Blacklist
   * @param {string} value
   * @returns {boolean}
   */
  #isBlacklistedRut(value) {
    const cleaned = this.#rutUtils.clean(value);
    if (!cleaned || cleaned.length < 2) return false;

    const numberPart = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);

    if (numberPart.length !== 7 && numberPart.length !== 8) return false;

    const first = numberPart[0];
    const allSame = numberPart.split('').every((c) => c === first);
    return allSame && dv === first;
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isAllowedRut(value) {
    return this.#rutUtils.validate(value) && !this.#isBlacklistedRut(value);
  }

  /**
   * Saves RUT to cart attributes and localStorage.
   * IMPORTANTE: Se guarda como "billing_rut" en note attributes.
   */
  async #saveRutToCart() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const rutInput = this.querySelector('#billing_rut');
      if (!(rutInput instanceof HTMLInputElement)) return;

      const rutFormatted = rutInput.value.trim();

      if (!rutFormatted) {
        this.#saveToLocalStorage('');
        await this.#updateCartAttribute('', abortController);
        return;
      }

      //  Solo guardar si es válido y NO está en blacklist
      if (!this.#isAllowedRut(rutFormatted)) {
        return;
      }

      // Guardar en localStorage como respaldo (solo válido)
      this.#saveToLocalStorage(rutFormatted);

      await this.#updateCartAttribute(rutFormatted, abortController);
    } catch (error) {
      this.#handleError(error, 'saveRutToCart');
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Saves RUT to localStorage.
   * @param {string} rutFormatted - The formatted RUT value
   */
  #saveToLocalStorage(rutFormatted) {
    try {
      if (!rutFormatted) {
        localStorage.removeItem('cart_billing_rut');
        return;
      }

      if (!this.#isAllowedRut(rutFormatted)) return;

      localStorage.setItem('cart_billing_rut', rutFormatted);
    } catch (e) {
      this.#handleError(e, 'saveToLocalStorage');
    }
  }

  /**
   * Updates cart attribute via API.
   * @param {string} rutValue - The RUT value to save
   * @param {AbortController} abortController - Abort controller for the request
   */
  async #updateCartAttribute(rutValue, abortController) {
    /** @type {Record<string, string>} */
    const body = {};
    // IMPORTANTE: Se guarda como "billing_rut" en note attributes
    body['attributes[billing_rut]'] = rutValue;

    const config = fetchConfig('json', {
      body: JSON.stringify(body),
    });

    await fetch(Theme.routes.cart_update_url, {
      ...config,
      signal: abortController.signal,
    });
  }

  /**
   * Handles errors with conditional logging.
   * @param {Error | unknown} error - The error to handle
   * @param {string} context - Context where the error occurred
   */
  #handleError(error, context) {
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.search.includes('debug=true'))
    ) {
      console.error(`[CartPrecheckout] ${context}:`, error);
    }
    // En producción, podrías enviar a un servicio de logging aquí
  }

  /**
   * Sets up RUT validation and formatting.
   */
  #setupRutValidation() {
    const rutInput = this.querySelector('#billing_rut');
    if (!(rutInput instanceof HTMLInputElement)) return;

    // Remove existing handlers if any to avoid duplicates
    if (this.#inputHandler) {
      rutInput.removeEventListener('input', this.#inputHandler);
    }
    if (this.#blurHandler) {
      rutInput.removeEventListener('blur', this.#blurHandler);
    }

    // Formatear RUT en tiempo real mientras el usuario escribe
    this.#inputHandler = (event) => {
      if (event.target instanceof HTMLInputElement) {
        this.#formatRutInput(event.target);
        this.#validateRut(event.target);
        this.#updateCartAttributesDebounced(event);
      }
    };
    rutInput.addEventListener('input', this.#inputHandler);

    // Validar y guardar al perder el foco
    this.#blurHandler = async (event) => {
      if (event.target instanceof HTMLInputElement) {
        this.#formatRutInput(event.target);
        const isValid = this.#validateRut(event.target);
        // Guardar inmediatamente al perder el foco si es válido o está vacío
        if (isValid || !event.target.value.trim()) {
          await this.#saveRutToCart();
        }
      }
    };
    rutInput.addEventListener('blur', this.#blurHandler);
  }

  /**
   * Formats RUT input in real-time using the provided format algorithm.
   * @param {HTMLInputElement} input - The RUT input element
   */
  #formatRutInput(input) {
    const value = input.value.trim();

    if (!value) {
      input.value = '';
      return;
    }

    // Limpiar el RUT usando la función clean
    const cleaned = this.#rutUtils.clean(value);

    if (!cleaned || cleaned.length < 2) {
      // Si no hay suficiente contenido, dejar como está
      return;
    }

    // Formatear usando el algoritmo proporcionado
    const formatted = this.#rutUtils.format(cleaned, { dots: true });
    input.value = formatted;
  }

  /**
   * Validates the RUT input and shows visual feedback.
   * @param {HTMLInputElement} input - The RUT input element
   * @returns {boolean} True if valid, false otherwise
   */
  #validateRut(input) {
    const value = input.value.trim();

    // Remover clases de error previas
    input.classList.remove('cart-precheckout__input--error');
    this.#removeRutError();

    // Si está vacío, no validar
    if (!value) return true;

    // Validar formato y dígito verificador usando el algoritmo proporcionado
    const isValid = this.#rutUtils.validate(value);

    if (!isValid) {
      input.classList.add('cart-precheckout__input--error');
      this.#showRutError('RUT inválido. Verifique el formato y el dígito verificador.');
      return false;
    }

    if (this.#isBlacklistedRut(value)) {
      input.classList.add('cart-precheckout__input--error');
      this.#showRutError('RUT inválido.');
      return false;
    }

    return true;
  }

  /**
   * Shows an error message for RUT validation.
   * @param {string} message - Error message to display
   */
  #showRutError(message) {
    const rutField = this.querySelector('#billing_rut')?.closest('.cart-precheckout__field');
    if (!rutField) return;

    // Remover error previo si existe
    this.#removeRutError();

    // Crear elemento de error
    const errorElement = document.createElement('span');
    errorElement.className = 'cart-precheckout__error';
    errorElement.id = 'rut-error-message';
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');

    rutField.appendChild(errorElement);
  }

  /**
   * Removes the RUT error message if it exists.
   */
  #removeRutError() {
    const errorElement = this.querySelector('#rut-error-message');
    if (errorElement) {
      errorElement.remove();
    }
  }

  /**
   * Validates RUT before checkout.
   * @returns {boolean} True if RUT is valid, false otherwise.
   */
  validateBeforeCheckout() {
    const rutInput = this.querySelector('#billing_rut');
    if (!(rutInput instanceof HTMLInputElement)) return true;

    const rutValue = rutInput.value.trim();

    const isRequired = this.dataset.requireRut === 'true' || rutInput.required;

    if (isRequired && !rutValue) {
      rutInput.classList.add('cart-precheckout__input--error');
      this.#showRutError('El RUT es requerido');
      requestAnimationFrame(() => {
        rutInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        rutInput.focus();
      });
      return false;
    }

    if (rutValue && !this.#validateRut(rutInput)) {
      requestAnimationFrame(() => {
        rutInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        rutInput.focus();
      });
      return false;
    }

    return true;
  }

  /**
   * Updates cart attributes with the correct name before checkout.
   * This ensures the RUT is saved as "billing_rut" in note attributes.
   */
  async updateCartAttributesBeforeCheckout() {
    // Primero guardar el RUT actual inmediatamente (sin debounce)
    await this.#saveRutToCart();

    // El RUT ya está guardado como billing_rut, no necesitamos hacer nada más
    // porque el campo HTML tiene name="attributes[billing_rut]" y se enviará automáticamente
  }

  /**
   * Restores RUT value from cart attributes, localStorage, or API when component loads.
   */
  #restoreRutValue() {
    requestAnimationFrame(() => {
      const rutInput = this.querySelector('#billing_rut');
      if (!(rutInput instanceof HTMLInputElement)) return;

      const rutFromCart = rutInput.value.trim();
      const rutFromStorage = this.#getRutFromStorage();

      if (rutFromStorage) {
        this.#restoreFromStorage(rutInput, rutFromStorage, rutFromCart);
        return;
      }

      if (rutFromCart) {
        this.#restoreFromCart(rutInput, rutFromCart);
      } else {
        this.#restoreRutFromCart();
      }
    });

    this.#setupRestoreListeners();
  }

  /**
   * Gets RUT from localStorage.
   * @returns {string | null} The RUT value or null
   */
  #getRutFromStorage() {
    try {
      const value = localStorage.getItem('cart_billing_rut');
      const rut = value?.trim() || null;

      if (rut && !this.#isAllowedRut(rut)) {
        localStorage.removeItem('cart_billing_rut');
        return null;
      }

      return rut;
    } catch (e) {
      this.#handleError(e, 'getRutFromStorage');
      return null;
    }
  }

  /**
   * Restores RUT from localStorage.
   * @param {HTMLInputElement} input - The input element
   * @param {string} storageValue - Value from localStorage
   * @param {string} cartValue - Value from cart (server)
   */
  #restoreFromStorage(input, storageValue, cartValue) {
    input.value = storageValue;
    this.#formatRutInput(input);
    this.#validateRut(input);

    // Si el valor del servidor está vacío, sincronizar con el carrito
    if (!cartValue) {
      this.#saveRutToCart();
    }
  }

  /**
   * Restores RUT from cart value.
   * @param {HTMLInputElement} input - The input element
   * @param {string} cartValue - Value from cart
   */
  #restoreFromCart(input, cartValue) {
    if (!this.#isAllowedRut(cartValue)) return;

    this.#formatRutInput(input);
    this.#saveToLocalStorage(cartValue);
    this.#validateRut(input);
  }

  /**
   * Sets up event listeners for restoring RUT value.
   */
  #setupRestoreListeners() {
    // Remove existing cart update handler if any
    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdateHandler);
    }

    this.#cartUpdateHandler = () => {
      setTimeout(() => {
        this.#setupRutValidation();

        const updatedRutInput = this.querySelector('#billing_rut');
        if (!(updatedRutInput instanceof HTMLInputElement)) return;

        const currentValue = updatedRutInput.value.trim();
        if (!currentValue) {
          const rutFromStorage = this.#getRutFromStorage();
          if (rutFromStorage) {
            this.#restoreFromStorage(updatedRutInput, rutFromStorage, currentValue);
            this.#saveRutToCart();
            return;
          }
          this.#restoreRutFromCart();
        } else {
          this.#formatRutInput(updatedRutInput);
          this.#saveToLocalStorage(currentValue);
          this.#validateRut(updatedRutInput);
        }
      }, 100);
    };

    document.addEventListener('cart:updated', this.#cartUpdateHandler);
  }

  /**
   * Restores RUT value from the current cart state via API.
   */
  async #restoreRutFromCart() {
    try {
      const response = await fetch(Theme.routes.cart_url + '.js');
      if (!response.ok) return;

      const cart = await response.json();
      const rutFromCart = cart.attributes?.billing_rut;

      if (!rutFromCart) return;

      if (!this.#isAllowedRut(rutFromCart)) return;

      const rutInput = this.querySelector('#billing_rut');
      if (!(rutInput instanceof HTMLInputElement)) return;

      rutInput.value = rutFromCart;
      this.#formatRutInput(rutInput);
      this.#saveToLocalStorage(rutFromCart);
      this.#validateRut(rutInput);
    } catch (error) {
      this.#handleError(error, 'restoreRutFromCart');
    }
  }

  /**
   * RUT validation and formatting utilities.
   * Basado en el algoritmo proporcionado.
   */
  #rutUtils = {
    /**
     * Limpia el RUT removiendo ceros al inicio y caracteres no válidos.
     * @param {string} rut - RUT a limpiar
     * @returns {string} RUT limpio en mayúsculas
     */
    clean: function (rut) {
      return typeof rut === 'string'
        ? rut.replace(/^0+|[^0-9kK]+/g, '').toUpperCase()
        : '';
    },

    /**
     * Valida el RUT con formato y dígito verificador.
     * @param {string} rut - RUT formateado (ej: "12.345.678-9" o "12345678-9")
     * @returns {boolean} True si el RUT es válido
     */
    validate: function (rut) {
      if (typeof rut !== 'string') return false;

      rut = this.clean(rut);
      if (!rut || rut.length < 2) return false;

      const numberPart = rut.slice(0, -1);
      const dv = rut.slice(-1);

      if (numberPart.length < 7) return false;
      if (!/^\d+$/.test(numberPart)) return false;
      if (!/^[0-9K]$/.test(dv)) return false;


      let t = parseInt(numberPart, 10);
      let m = 0;
      let s = 1;

      while (t > 0) {
        s = (s + (t % 10) * (9 - (m++ % 6))) % 11;
        t = Math.floor(t / 10);
      }

      const expected = s > 0 ? String(s - 1) : 'K';
      return expected === dv;
    },

    /**
     * Formatea el RUT con puntos y guion.
     * @param {string} rut - RUT a formatear
     * @param {Object} options - Opciones de formateo
     * @param {boolean} options.dots - Si true, agrega puntos (default: true)
     * @returns {string} RUT formateado
     */
    format: function (rut, options = { dots: true }) {
      if (!rut) {
        return '';
      }

      rut = this.clean(rut);
      if (!rut || rut.length < 2) {
        return rut;
      }

      let result;
      if (options.dots) {
        // Separar número y dígito verificador
        const numberPart = rut.slice(0, -1);
        const checkDigit = rut.slice(-1);

        // Dividir en grupos de 3 desde la derecha
        const groups = [];
        for (let i = numberPart.length; i > 0; i -= 3) {
          const start = Math.max(0, i - 3);
          groups.unshift(numberPart.slice(start, i));
        }

        result = groups.join('.') + '-' + checkDigit;
      } else {
        result = rut.slice(0, -1) + '-' + rut.slice(-1);
      }

      return result;
    },

    /**
     * Calcula el dígito verificador de un RUT.
     * @param {string} input - RUT sin dígito verificador
     * @returns {string} Dígito verificador calculado ('K' o número)
     */
    getCheckDigit: function (input) {
      const rut = Array.from(this.clean(input), Number);

      if (rut.length === 0 || rut.includes(NaN)) {
        return '';
      }

      const modulus = 11;
      const initialValue = 0;
      const sumResult = rut
        .reverse()
        .reduce(
          (accumulator, currentValue, index) =>
            accumulator + currentValue * ((index % 6) + 2),
          initialValue
        );

      const checkDigit = modulus - (sumResult % modulus);

      if (checkDigit === 10) {
        return 'K';
      } else if (checkDigit === 11) {
        return '0';
      } else {
        return checkDigit.toString();
      }
    },
  };
}

if (!customElements.get('cart-precheckout')) {
  customElements.define('cart-precheckout', CartPrecheckout);
}
